import React, { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import FingerprintRoundedIcon from '@mui/icons-material/FingerprintRounded';
import { useAuth } from '../AuthContext';
import api from '../api';
import { confirmarComBiometria, situacaoLocal } from '../utils/biometria';

const hoje = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * Cadeado para abrir/fechar a pelada (apenas Admin/Diretor).
 * Abrir pede data e horário; fechar pede confirmação e, se houver biometria ativa no aparelho, a biometria.
 * `pelada` é a pelada aberta (ou null); `onChange()` é chamado depois de abrir ou fechar.
 */
const PeladaLockButton = ({ pelada, onChange, onMessage }) => {
  const { userLogado } = useAuth();
  const [dialogo, setDialogo] = useState(null); // 'abrir' | 'fechar'
  const [data, setData] = useState(hoje());
  const [horario, setHorario] = useState('20:00');
  const [salvando, setSalvando] = useState(false);
  const podeGerenciar = userLogado && (userLogado.perfil === 'Admin' || userLogado.perfil === 'Diretor');

  if (!podeGerenciar) return null;
  const aberta = Boolean(pelada && pelada._id);
  const pedeBiometria = situacaoLocal(userLogado._id) === 'ativa';

  const avisar = (message, severity = 'success') => onMessage && onMessage(message, severity);

  const abrir = async () => {
    setSalvando(true);
    try {
      await api.post('createPelada', { date: data, time: horario });
      setDialogo(null);
      onChange && (await onChange());
      avisar('Pelada aberta! Os jogadores foram notificados.');
    } catch (error) {
      avisar(error.response?.data?.error || 'Não foi possível abrir a pelada.', 'error');
    }
    setSalvando(false);
  };

  const fechar = async () => {
    setSalvando(true);
    const confirmacao = await confirmarComBiometria(userLogado._id);
    if (!confirmacao.ok) {
      avisar(confirmacao.message, 'error');
      setSalvando(false);
      return;
    }
    try {
      await api.post('closedPelada', { pelada_id: pelada._id, biometria: confirmacao.biometria || undefined });
      setDialogo(null);
      onChange && (await onChange());
      avisar('Pelada fechada.');
    } catch (error) {
      avisar(error.response?.data?.error || 'Não foi possível fechar a pelada.', 'error');
    }
    setSalvando(false);
  };

  return (
    <>
      <Button
        variant={aberta ? 'contained' : 'outlined'}
        color={aberta ? 'success' : 'inherit'}
        startIcon={aberta ? <LockOpenRoundedIcon /> : <LockRoundedIcon />}
        onClick={() => setDialogo(aberta ? 'fechar' : 'abrir')}
        aria-label={aberta ? 'Fechar pelada' : 'Abrir pelada'}
      >
        {aberta ? 'Pelada aberta' : 'Pelada fechada'}
      </Button>

      <Dialog open={dialogo === 'abrir'} onClose={() => !salvando && setDialogo(null)} fullWidth maxWidth="xs">
        <DialogTitle>Abrir pelada</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Todos os jogadores ativos recebem uma notificação para confirmar presença.
          </DialogContentText>
          <Stack spacing={2}>
            <TextField type="date" label="Data" value={data} onChange={(e) => setData(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField type="time" label="Horário" value={horario} onChange={(e) => setHorario(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogo(null)} color="inherit" disabled={salvando}>Cancelar</Button>
          <Button onClick={abrir} variant="contained" startIcon={<LockOpenRoundedIcon />} disabled={salvando || !data || !horario}>Abrir pelada</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogo === 'fechar'} onClose={() => !salvando && setDialogo(null)}>
        <DialogTitle>Fechar a pelada?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            A pelada é encerrada: ninguém mais confirma presença nem faz check-in, e a avaliação dos jogadores abre 30 minutos depois.
            {pedeBiometria && ' Você vai confirmar com a biometria deste aparelho.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogo(null)} color="inherit" disabled={salvando}>Cancelar</Button>
          <Button onClick={fechar} variant="contained" color="error" startIcon={pedeBiometria ? <FingerprintRoundedIcon /> : <LockRoundedIcon />} disabled={salvando}>
            Fechar pelada
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default PeladaLockButton;
